<template>
  <!-- Two columns: a coloured sidebar (photo, contact, skills, languages, certifications) and the
       main column (name, summary, experience, education, projects). The sidebar's colour is
       drawn by the sheet behind this, so it runs the full height of every page. -->
  <div class="flex items-stretch" :style="{ flexDirection: side.left ? 'row' : 'row-reverse' }">
    <aside class="min-w-0 py-1" :style="{ width: `${SIDEBAR_WIDTH}%`, color: side.color, padding: '4px 22px' }">
      <div v-if="showPicture" class="flex justify-center mb-4"><ResumePhoto :url="pictureUrl" :shape="settings.picture_shape" :size="108" /></div>

      <div v-if="contacts.length" class="mb-5">
        <h2 :class="HEADING" :style="sideHeadingStyle">Contact</h2>
        <ResumeContacts :contacts="contacts" stack />
      </div>

      <section v-for="section in sideSections" :key="section.id" class="resume-section mb-5">
        <h2 :class="HEADING" :style="sideHeadingStyle">{{ section.title }}</h2>
        <ResumeSection :section="section" :content="content" :accent="accent" layout="stacked" />
      </section>
    </aside>

    <main class="min-w-0 flex-1" :style="{ padding: `4px ${padding}px` }">
      <header class="mb-1">
        <h1 class="text-[2em] font-bold leading-tight" :style="{ color: accent }">{{ basics.full_name || 'Your name' }}</h1>
        <p v-if="basics.headline" class="mt-0.5 text-[1.1em] font-medium text-gray-700">{{ basics.headline }}</p>
      </header>

      <div :style="{ display: 'flex', flexDirection: 'column', gap: `${gap}px`, marginTop: `${gap}px` }">
        <section v-for="section in mainSections" :key="section.id" class="resume-section">
          <h2 class="mb-1.5 pb-0.5 text-[0.82em] font-bold uppercase tracking-[0.12em] border-b" :style="{ color: accent, borderColor: accent }">{{ section.title }}</h2>
          <ResumeSection :section="section" :content="content" :accent="accent" />
        </section>
      </div>

      <p v-if="!mainSections.length && !basics.full_name" class="mt-10 text-center text-gray-400">Your resume appears here as you type.</p>
    </main>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import ResumeContacts from './ResumeContacts.vue'
import ResumePhoto from './ResumePhoto.vue'
import ResumeSection from './ResumeSection.vue'
import { SIDEBAR_WIDTH, gapOf, paddingOf, sidebarOf } from './resumeLayout.js'
import { useResumeView } from './resumeView.js'

const props = defineProps({
  content: { type: Object, required: true },
  template: { type: String, default: 'sidebar' },
  pictureUrl: { type: String, default: null },
})

const SIDE_KEYS = ['skills', 'languages', 'certifications']
const HEADING = 'mb-1.5 pb-0.5 text-[0.82em] font-bold uppercase tracking-[0.12em] border-b'

const { basics, settings, accent, contacts, sections, showPicture } = useResumeView(props)
const side = computed(() => sidebarOf(props.template, settings.value))
const gap = computed(() => gapOf(settings.value))
const padding = computed(() => paddingOf(settings.value))

const sideSections = computed(() => sections.value.filter((section) => SIDE_KEYS.includes(section.key)))
const mainSections = computed(() => sections.value.filter((section) => !SIDE_KEYS.includes(section.key)))

// The sidebar's headings take the sidebar's own text colour, with a softer line under them.
const sideHeadingStyle = computed(() => ({
  color: props.template === 'twocolumn' ? accent.value : 'inherit',
  borderColor: props.template === 'twocolumn' ? accent.value : 'color-mix(in srgb, currentColor 35%, transparent)',
}))
</script>
